import { css, cva } from '@styled-system/css';
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Euro, Package, Percent, ShoppingBag, TrendingDown, TrendingUp, Users } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { dsTokens } from '@/shared/theme/tokens';

import {
  useGetVeilleDailyStatsQuery,
  useGetVeilleStatsComparisonQuery,
  useGetVeilleWeeklyStatsQuery,
} from '@/shared/hooks/queries/generated';

import type { VeilleView } from '../veilleTypes';

export type VeilleKpi = 'sales' | 'newItems' | 'sellers' | 'averagePrice' | 'sellThroughRate';

const KPI_IS_VOLUME: Record<VeilleKpi, boolean> = {
  sales: true,
  newItems: true,
  sellers: true,
  averagePrice: false,
  sellThroughRate: false,
};

/** Lundi (UTC) de la semaine contenant le jour donné ("YYYY-MM-DD"). */
const mondayOf = (day: string): string => {
  const date = new Date(`${day}T00:00:00Z`);
  const weekday = date.getUTCDay(); // 0 = dimanche
  date.setUTCDate(date.getUTCDate() - (weekday === 0 ? 6 : weekday - 1));
  return date.toISOString().slice(0, 10);
};

type DsTokens = (typeof dsTokens)['light'];

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// ============================================================================
// Onglet Statistiques de la veille (API réelle) :
// - un jour/semaine de pause est un TROU dans la courbe (null non interpolé,
//   jamais une barre à zéro) ;
// - averagePrice/sellThroughRate valent null quand leur dénominateur est vide
//   (affichés « — », jamais 0) ; sellThroughRate n'est JAMAIS plafonné ;
// - la semaine en cours est marquée « en cours » (comparée à rien) ;
// - deltas : null = « pas de comparaison possible », affiché « — », jamais 0.
// ============================================================================

const wrap = css({ display: 'flex', flexDirection: 'column', gap: '5', paddingTop: '3' });

const toolbar = css({ display: 'flex', alignItems: 'center', gap: '2' });

const grid = css({
  display: 'grid',
  gridTemplateColumns: { base: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
  gap: '5',
});

const chartCard = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  minWidth: 0,
});

const cardHeader = css({ display: 'flex', alignItems: 'flex-start', gap: '4' });

const cardIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '30px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  color: 'text.secondary',
  flexShrink: 0,
});

const cardLabel = css({ fontSize: 'caption', color: 'text.tertiary' });

const cardValue = css({
  fontFamily: 'mono',
  fontSize: '19px',
  lineHeight: 1.15,
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.5px',
});

const delta = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    fontFamily: 'mono',
    fontSize: 'caption',
    fontWeight: 'bold',
    marginLeft: 'auto',
    flexShrink: 0,
    paddingY: '1',
    paddingX: '2.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  variants: {
    tone: {
      up: { color: 'success.default', bg: 'success.soft', borderColor: 'success.border' },
      down: { color: 'error.default', bg: 'error.soft', borderColor: 'error.border' },
      flat: { color: 'text.tertiary', bg: 'surface.sunken', borderColor: 'border.default' },
    },
  },
});

const KPI_ICONS: Record<VeilleKpi, ReactNode> = {
  sales: <ShoppingBag size={14} strokeWidth={1.75} />,
  newItems: <Package size={14} strokeWidth={1.75} />,
  averagePrice: <Euro size={14} strokeWidth={1.75} />,
  sellers: <Users size={14} strokeWidth={1.75} />,
  sellThroughRate: <Percent size={14} strokeWidth={1.75} />,
};

// Une teinte DS par KPI : le tableau de bord se lit d'un coup d'œil
const KPI_COLORS: Record<VeilleKpi, (ds: DsTokens) => string> = {
  sales: (ds) => ds['brand.primary'],
  newItems: (ds) => ds['warning.default'],
  averagePrice: (ds) => ds['warning.default'],
  sellers: (ds) => ds['success.default'],
  sellThroughRate: (ds) => ds['brand.primary'],
};

const SECONDARY_KPIS: VeilleKpi[] = ['averagePrice', 'sellers', 'sellThroughRate'];

const formatKpiValue = (kpi: VeilleKpi, value: number | null | undefined, locale: string): string => {
  if (value == null) return '—';
  if (kpi === 'averagePrice') return `${value.toLocaleString(locale, { maximumFractionDigits: 2 })} €`;
  if (kpi === 'sellThroughRate') return `${value.toLocaleString(locale, { maximumFractionDigits: 1 })} %`;
  return value.toLocaleString(locale);
};

// ─── Points affichables : période = jours OU semaines, valeurs nullables ────

interface DisplayPoint {
  label: string;
  values: Record<VeilleKpi, number | null>;
  /** Semaine incomplète (pause) : exclue des comparaisons. */
  partial?: boolean;
  /** Semaine en cours : jamais comparée aux semaines closes. */
  current?: boolean;
}

/** Delta % entre les deux derniers points comparables ; null = pas comparable. */
const computeDelta = (points: DisplayPoint[], kpi: VeilleKpi): number | null => {
  const comparable = points.filter((point) => !point.partial && !point.current && point.values[kpi] != null);
  if (comparable.length < 2) return null;
  const current = comparable[comparable.length - 1].values[kpi] as number;
  const previous = comparable[comparable.length - 2].values[kpi] as number;
  if (previous === 0) return current === 0 ? 0 : null; // N/0 : indéfini, jamais Infinity
  return ((current - previous) / previous) * 100;
};

const DeltaBadge: FC<{ deltaPct: number | null; locale: string }> = ({ deltaPct, locale }) => {
  if (deltaPct == null) {
    return <span className={delta({ tone: 'flat' })}>—</span>;
  }
  const tone = Math.abs(deltaPct) < 0.5 ? 'flat' : deltaPct > 0 ? 'up' : 'down';
  return (
    <span className={delta({ tone })}>
      {tone === 'up' ? (
        <TrendingUp size={11} strokeWidth={2} />
      ) : tone === 'down' ? (
        <TrendingDown size={11} strokeWidth={2} />
      ) : null}
      {deltaPct > 0 ? '+' : ''}
      {deltaPct.toLocaleString(locale, { maximumFractionDigits: 1 })} %
    </span>
  );
};

// ─── Options chart partagées (trous non interpolés : spanGaps false) ────────

const buildOptions = (kpi: VeilleKpi, ds: DsTokens, locale: string): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  spanGaps: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: ds['surface.elevated'],
      titleColor: ds['text.primary'],
      bodyColor: ds['text.secondary'],
      borderColor: ds['border.default'],
      borderWidth: 1,
      callbacks: {
        label: (context) => formatKpiValue(kpi, context.parsed.y, locale),
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: ds['text.tertiary'], font: { size: 10 }, maxTicksLimit: 8 },
      border: { color: ds['border.subtle'] },
    },
    y: {
      grid: { color: ds['border.subtle'] },
      ticks: {
        color: ds['text.tertiary'],
        font: { size: 10 },
        maxTicksLimit: 5,
        callback: (value) => formatKpiValue(kpi, Number(value), locale),
      },
      border: { display: false },
      beginAtZero: KPI_IS_VOLUME[kpi],
    },
  },
});

const lineDataset = (values: (number | null)[], color: string, ds: DsTokens, label?: string) => ({
  label,
  data: values,
  borderColor: color,
  backgroundColor: `${color}1f`,
  fill: true,
  tension: 0.35,
  pointRadius: values.length > 14 ? 0 : 2.5,
  pointHoverRadius: 4,
  pointBackgroundColor: color,
  pointBorderColor: ds['surface.default'],
  borderWidth: 2,
});

// ─── Carte comparative ventes détectées / nouveaux articles ─────────────────

const legendDotLabel = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const legendDot = css({ width: '8px', height: '8px', borderRadius: 'full', flexShrink: 0 });

const SalesVsNewItemsCard: FC<{
  points: DisplayPoint[];
  locale: string;
  ds: DsTokens;
  deltaFor: (kpi: VeilleKpi) => number | null;
}> = ({ points, locale, ds, deltaFor }) => {
  const { t } = useTranslation();
  // Solo d'une courbe au clic sur sa stat (re-clic = retour aux deux)
  const [focus, setFocus] = useState<'sales' | 'newItems' | null>(null);

  const data: ChartData<'line'> = {
    labels: points.map((point) => point.label),
    datasets: (['sales', 'newItems'] as const).map((kpi) => ({
      ...lineDataset(points.map((point) => point.values[kpi]), KPI_COLORS[kpi](ds), ds, t(`veille.kpi.${kpi}`)),
      hidden: focus != null && focus !== kpi,
    })),
  };

  const statFor = (kpi: 'sales' | 'newItems') => {
    const color = KPI_COLORS[kpi](ds);
    const lastWithValue = [...points].reverse().find((point) => point.values[kpi] != null);
    const dimmed = focus != null && focus !== kpi;
    return (
      <button
        type="button"
        onClick={() => setFocus((previousFocus) => (previousFocus === kpi ? null : kpi))}
        title={t('veille.focusHint')}
        className={css({
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'opacity 120ms ease',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5',
          minWidth: 0,
        })}
        style={{ opacity: dimmed ? 0.4 : 1 }}
      >
        <span className={legendDotLabel}>
          <span className={legendDot} style={{ background: color }} />
          {t(`veille.kpi.${kpi}`)}
        </span>
        <span className={css({ display: 'inline-flex', alignItems: 'baseline', gap: '3' })}>
          <span className={cardValue}>{formatKpiValue(kpi, lastWithValue?.values[kpi], locale)}</span>
          <DeltaBadge deltaPct={deltaFor(kpi)} locale={locale} />
        </span>
      </button>
    );
  };

  return (
    <div className={chartCard}>
      <div className={css({ display: 'flex', alignItems: 'flex-start', gap: '10', flexWrap: 'wrap' })}>
        {statFor('sales')}
        {statFor('newItems')}
      </div>
      <div style={{ height: 250 }}>
        <Line data={data} options={buildOptions('sales', ds, locale)} />
      </div>
    </div>
  );
};

// ─── Carte mono-KPI ─────────────────────────────────────────────────────────

const KpiChartCard: FC<{
  kpi: VeilleKpi;
  points: DisplayPoint[];
  locale: string;
  ds: DsTokens;
  hint?: string;
  deltaFor: (kpi: VeilleKpi) => number | null;
}> = ({ kpi, points, locale, ds, hint, deltaFor }) => {
  const { t } = useTranslation();
  const lastWithValue = [...points].reverse().find((point) => point.values[kpi] != null);
  const color = KPI_COLORS[kpi](ds);

  const data: ChartData<'line'> = {
    labels: points.map((point) => point.label),
    datasets: [lineDataset(points.map((point) => point.values[kpi]), color, ds)],
  };

  return (
    <div className={chartCard}>
      <div className={cardHeader}>
        <span className={cardIcon}>{KPI_ICONS[kpi]}</span>
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '0.5', minWidth: 0 })}>
          <span className={cardLabel}>
            {t(`veille.kpi.${kpi}`)}
            {hint ? ` · ${hint}` : ''}
          </span>
          <span className={cardValue}>{formatKpiValue(kpi, lastWithValue?.values[kpi], locale)}</span>
        </div>
        <DeltaBadge deltaPct={deltaFor(kpi)} locale={locale} />
      </div>
      <div style={{ height: 170 }}>
        <Line data={data} options={buildOptions(kpi, ds, locale)} />
      </div>
    </div>
  );
};

// ─── Onglet ─────────────────────────────────────────────────────────────────

export const VeilleStats: FC<{ veille: VeilleView }> = ({ veille }) => {
  const { t, i18n } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';

  const [period, setPeriod] = useState<'daily' | 'weekly'>('daily');

  // Pendant le backfill des 7 jours, la série se remplit : on resond
  const historyPending = veille.historyState === 'queued' || veille.historyState === 'running';
  const statsOptions = {
    staleTime: 60_000,
    refetchInterval: historyPending ? 10_000 : false,
  } as const;

  // Défauts serveur : 30 derniers jours / 12 dernières semaines
  const { data: dailyData } = useGetVeilleDailyStatsQuery({ veilleId: veille.id, from: null, to: null }, statsOptions);
  const { data: weeklyData } = useGetVeilleWeeklyStatsQuery({ veilleId: veille.id, from: null, to: null }, statsOptions);
  // Badges de variation de l'onglet Jour — même plage que la série (défaut 30 j)
  const { data: comparisonData } = useGetVeilleStatsComparisonQuery({ veilleId: veille.id, from: null, to: null }, statsOptions);
  const comparison = comparisonData?.getVeilleStatsComparison ?? null;

  const daily = useMemo(() => dailyData?.getVeilleDailyStats ?? [], [dailyData]);

  // Jour : axe CALENDAIRE complet — un jour absent (pause) devient un null,
  // jamais un zéro, et spanGaps:false le laisse en trou dans la courbe
  const dailyPoints = useMemo((): DisplayPoint[] => {
    const byDay = new Map(daily.map((point) => [point.day, point]));
    const points: DisplayPoint[] = [];
    const today = new Date();
    for (let i = 30; i >= 1; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const day = date.toISOString().slice(0, 10);
      const point = byDay.get(day);
      points.push({
        label: new Date(`${day}T00:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
        values: {
          sales: point?.sales ?? null,
          newItems: point?.newItems ?? null,
          sellers: point?.sellers ?? null,
          averagePrice: point?.averagePrice ?? null,
          sellThroughRate: point?.sellThroughRate ?? null,
        },
      });
    }
    return points;
  }, [daily, locale]);

  // Semaine : weekStart est un LUNDI UTC, à formater sans décalage de fuseau ;
  // la semaine en cours et les semaines trouées sont marquées et non comparées
  const weeklyPoints = useMemo((): DisplayPoint[] => {
    const currentMonday = mondayOf(new Date().toISOString().slice(0, 10));
    return (weeklyData?.getVeilleWeeklyStats ?? []).map((week) => {
      const isCurrent = week.weekStart === currentMonday;
      const label = new Date(`${week.weekStart}T00:00:00Z`).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });
      return {
        label: isCurrent
          ? `${label} ${t('veille.weekCurrent')}`
          : week.daysCounted < 7
            ? `${label} (${t('veille.weekPartial', { count: week.daysCounted })})`
            : label,
        values: {
          sales: week.sales,
          newItems: week.newItems,
          sellers: week.sellers,
          averagePrice: week.averagePrice ?? null,
          sellThroughRate: week.sellThroughRate ?? null,
        },
        partial: week.daysCounted < 7,
        current: isCurrent,
      };
    });
  }, [weeklyData, locale, t]);

  const points = period === 'weekly' ? weeklyPoints : dailyPoints;

  // Onglet Jour : les badges viennent de getVeilleStatsComparison (période de
  // même longueur immédiatement adjacente) ; onglet Semaine : comparaison
  // client entre les deux dernières semaines COMPLÈTES (contrat §2bis.7)
  const serverDelta = (kpi: VeilleKpi): number | null => {
    if (!comparison) return null;
    const byKpi: Record<VeilleKpi, number | null | undefined> = {
      sales: comparison.salesVariation,
      newItems: comparison.newItemsVariation,
      sellers: comparison.sellersVariation,
      averagePrice: comparison.averagePriceVariation,
      sellThroughRate: comparison.sellThroughRateVariation,
    };
    return byKpi[kpi] ?? null;
  };
  const deltaFor = (kpi: VeilleKpi): number | null =>
    period === 'daily' ? serverDelta(kpi) : computeDelta(points, kpi);

  return (
    <div className={wrap}>
      <div className={toolbar}>
        <ChipToggle selected={period === 'daily'} onChange={() => setPeriod('daily')}>
          {t('veille.periodDaily')}
        </ChipToggle>
        <ChipToggle selected={period === 'weekly'} onChange={() => setPeriod('weekly')}>
          {t('veille.periodWeekly')}
        </ChipToggle>
        {veille.state === 'paused' && (
          <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.statsPausedNote')}</span>
        )}
      </div>

      {/* Ventes détectées vs nouveaux articles : comparaison sur le même graphe */}
      <SalesVsNewItemsCard points={points} locale={locale} ds={ds} deltaFor={deltaFor} />

      {/* Les 3 autres KPI en grille — sellers = flux quotidien / cumul hebdo */}
      <div className={grid}>
        {SECONDARY_KPIS.map((kpi) => (
          <KpiChartCard
            key={kpi}
            kpi={kpi}
            points={points}
            locale={locale}
            ds={ds}
            deltaFor={deltaFor}
            hint={
              kpi === 'sellers'
                ? period === 'weekly'
                  ? t('veille.sellersWeeklyHint')
                  : t('veille.sellersDailyHint')
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};
